import { useEffect, useState } from 'react';

import { useDisplayContext } from '~src/context/DisplayContext';

import { useBbrLookup } from '~src/hooks/graphql/useBbrLookup';
import { useEnergyLabel } from '~src/hooks/graphql/useEnergyLabel';
import { useFetchNewOfferExpenses } from '~src/hooks/graphql/useExpense';
import { useFetchProducts } from '~src/hooks/graphql/useProduct';
import { useOverrideAddressByID } from '~src/hooks/useDAWA/useOverrideAddressByID';
import { useTakeover } from '~src/hooks/useTakeover';
import { FuelType, Residence, ResidenceInput } from '~src/model';
import { useAnalytics } from '~src/tracking/ga';
import { DEFAULT_CONSUMPTION_PROFILE_KEY, estimatePrimaryHeating } from '~src/util';

import { useCalculatorProducts } from './useCalculatorProducts';
import { useEnergyOptionsContext } from '~src/context/EnergyOptionsContext';

export const usePublicCalculator = (isStandalone?: boolean) => {
  const { isMobile } = useDisplayContext();

  const { options } = useEnergyOptionsContext();

  const { address, setAddress, addressID } = useOverrideAddressByID();

  const { stepCompleted } = useAnalytics();

  const { outdoorUnits, indoorUnits } = useFetchProducts({}, { include: true }, { include: true });

  const newOfferExpenses = useFetchNewOfferExpenses();

  const [residence, setResidence] = useState<Partial<ResidenceInput>>({
    consumptionProfile: DEFAULT_CONSUMPTION_PROFILE_KEY,
  });

  const [visitedStageMap, setVisitedStageMap] = useState<Record<number, boolean>>({});
  const [openStageMap, setOpenStageMap] = useState<Record<number, boolean>>({ 1: true });
  const [stage, setStage] = useState<number>(0);

  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [showResidenceInputFields, setShowResidenceInputFields] = useState(false);

  const [financing, setFinancing] = useState<boolean>(false);

  const takeover = useTakeover(residence, outdoorUnits);

  useBbrLookup(address, setResidence);

  const { offerProducts, setOfferProducts } = useCalculatorProducts({
    residence: residence as Residence,
    outdoorUnits,
    indoorUnits,
  });

  const { primaryHeating, area = 0, builtYear } = residence ?? {};
  const { fuelType } = primaryHeating ?? {};

  useEffect(
    function setEstimatedHeatingUsage() {
      if (!fuelType) {
        return;
      }

      const estimatedPrimaryHeating = estimatePrimaryHeating(fuelType as FuelType, area, builtYear ?? 1980, options);
      setResidence({ ...residence, primaryHeating: estimatedPrimaryHeating });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fuelType]
  );

  useEnergyLabel(addressID?.toString(), residence, setResidence);

  const updateVisitedStageMap = (newStage: number) => {
    setVisitedStageMap(map => ({ ...map, [newStage]: true }));
    stepCompleted(isMobile ? newStage - 1 : newStage);
  };

  return {
    residence,
    setResidence,
    address,
    setAddress,
    outdoorUnits: outdoorUnits ?? [],
    showResidenceInputFields,
    setShowResidenceInputFields,
    indoorUnits: indoorUnits ?? [],
    financing,
    setFinancing,
    visitedStageMap,
    setVisitedStageMap: updateVisitedStageMap,
    isStandalone,
    openStageMap,
    setOpenStageMap,
    stage,
    setStage,
    newOfferExpenses,
    offerProducts,
    setOfferProducts,
    headerExpanded,
    setHeaderExpanded,
    takeover,
  };
};
